import { API_DATE_FORMAT, DATE_FORMAT } from "@/app/utils/constants/constants";
import { getWeekdays } from "@/app/utils/date/getWeekdays";
import { MAX_STAY_DURATION_DAYS } from "@/constants/stay";
import type { FlightSearchRequest } from "@/types/flights";
import dayjs from "@dayjs";
import { Calendar } from "antd";
import { Dayjs } from "dayjs";
import { useLocale, useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import React, {
  Fragment,
  useEffect,
  useRef,
  useState,
  type MouseEventHandler,
} from "react";
import CalendarModal from "../../DatePicker/CalendarModal";
import { CalendarsIcon, Cross } from "../../Icons";

type CalendarHeaderProps = {
  date: Dayjs;
};

const CalendarHeader = ({ date }: CalendarHeaderProps) => (
  <div className="text-center text-lg font-bold shadow dark:bg-sleap-gray-950 bg-lightwhite">
    {date.format("MMMM YYYY")}
  </div>
);

type MonthCalendarProps = {
  monthOffset: number;
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
  date: [Dayjs | null, Dayjs | null];
  setDate: React.Dispatch<React.SetStateAction<[Dayjs | null, Dayjs | null]>>;
  departOnly?: boolean;
};

const MonthCalendar = ({
  monthOffset,
  date,
  setDate,
  departOnly,
}: MonthCalendarProps) => {
  const month = dayjs().add(monthOffset, "month");

  const isDateSelectable = (currentDate: Dayjs) => {
    const startOfToday = dayjs().startOf("day");
    const isDateBeforeToday = currentDate.isBefore(startOfToday, "day");

    // disallow selection of past dates.
    if (isDateBeforeToday) {
      return false;
    }

    if (departOnly) {
      return true;
    }

    const isStartDateSelected = Boolean(date[0]);
    const isEndDateSelected = Boolean(date[1]);

    // if a start date is already selected but not an end date, handle he range.
    if (isStartDateSelected && !isEndDateSelected) {
      const startToDateDiff = currentDate.diff(date[0], "day");

      // allow selection only if the curr date is after the start date and 30 days range
      const isAfterStartDate = startToDateDiff > 0;
      const isWithin30DaysRange = startToDateDiff <= MAX_STAY_DURATION_DAYS;

      return isWithin30DaysRange;
    }

    // if both start and end dates are selected, or no start date is not selected allow any date to be selected
    return !isStartDateSelected || (isStartDateSelected && isEndDateSelected);
  };

  const fullCellRender = (current: Dayjs) => {
    const isSelectedStart =
      date[0] &&
      current.isSame(date[0], "day") &&
      current.isSame(date[0], "month") &&
      current.isSame(date[0], "year");
    const isSelectedEnd =
      date[1] &&
      current.isSame(date[1], "day") &&
      current.isSame(date[1], "month") &&
      current.isSame(date[1], "year");
    const isInRange =
      date[0] &&
      date[1] &&
      current.isAfter(date[0], "day") &&
      current.isBefore(date[1], "day");
    const isCurrentMonth = current.month() === month.month();

    let cellClass = "";

    if (isSelectedStart) {
      cellClass = "start-date";
    } else if (isSelectedEnd) {
      cellClass = "end-date";
    } else if (isInRange) {
      cellClass = "range-date";
    }
    return (
      <div
        className={`ant-picker-cell-inner ${cellClass} ${!isCurrentMonth && "ant-picker-cell-not-in-view"} !min-w-10 h-14 pt-2 flex justify-center items-center}`}
      >
        {current.date()}
      </div>
    );
  };

  const onSelect = (_date: Dayjs) => {
    if (departOnly) {
      setDate([dayjs(_date, DATE_FORMAT), null]);
    } else {
      if (!date[0] || (date[0] && date[1])) {
        // No date or both dates already selected: start a new selection
        setDate([dayjs(_date, DATE_FORMAT), null]);
      } else {
        const fromDate: Dayjs = date[0]!;
        const toDate = dayjs(_date, DATE_FORMAT);

        if (toDate.isBefore(fromDate, "day")) {
          // If selected date is before first date, set as new first date
          setDate([toDate, null]);
        } else {
          // If selected date is after first date, set as second date
          setDate([fromDate, toDate]);
        }
      }
    }
  };

  return (
    <div>
      <CalendarHeader date={month} />
      <Calendar
        fullscreen={false}
        headerRender={() => null}
        value={month}
        disabledDate={(current) => !isDateSelectable(current)}
        onSelect={onSelect}
        fullCellRender={fullCellRender}
      />
    </div>
  );
};

type Props = {
  departOnly?: boolean;
  departDate: string;
  returnDate: string;
  updateFlightField: (
    flightId: string,
    updatedFields: Partial<FlightSearchRequest>
  ) => void;
  flightId: string;
};

const MobileCalendars = ({
  departOnly,
  departDate,
  returnDate,
  updateFlightField,
  flightId,
}: Props) => {
  const locale = useLocale();
  const t = useTranslations();
  const [isVisible, setIsVisible] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [date, setDate] = useState<[Dayjs | null, Dayjs | null]>([
    departDate ? dayjs(departDate, API_DATE_FORMAT) : null,
    returnDate ? dayjs(returnDate, API_DATE_FORMAT) : null,
  ]);

  const weekdays = getWeekdays(t);

  const { resolvedTheme } = useTheme();

  useEffect(() => {
    if (departOnly) {
      setDate([departDate ? dayjs(departDate, API_DATE_FORMAT) : null, null]);
    } else {
      setDate([
        departDate ? dayjs(departDate, API_DATE_FORMAT) : null,
        returnDate ? dayjs(returnDate, API_DATE_FORMAT) : null,
      ]);
    }
  }, [departDate, returnDate]);

  const toggleVisibility = () => {
    if (isVisible) {
      setScrollPosition(containerRef.current?.scrollTop ?? 0);
    } else {
      if (containerRef.current) {
        containerRef.current.scrollTop = scrollPosition;
      }
    }
    setIsVisible(!isVisible);
  };

  const formatDisplayDate = (date: Dayjs | null, locale: string): string => {
    if (!date) {
      return "";
    }

    if (locale === "en") {
      return dayjs(date).format("ddd, MMM D");
    }

    return dayjs(date).format("YYYY-MM-DD");
  };

  useEffect(() => {
    if (isVisible && containerRef.current) {
      containerRef.current.scrollTop = scrollPosition;
    }
  }, [isVisible, scrollPosition]);

  const handleConfirm: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();

    if (date[0]) {
      if (!departOnly && !date[1]) {
        // Only start date selected, auto-select end date as start + 1 day
        const autoEndDate = date[0].add(1, "day");
        setDate([date[0], autoEndDate]);
        updateFlightField(flightId, {
          departDate: date[0].format(API_DATE_FORMAT),
          returnDate: autoEndDate.format(API_DATE_FORMAT),
        });
        setIsVisible(false);
        return;
      }

      const formattedDepartDate = date[0].format(API_DATE_FORMAT);
      const formattedReturnDate = date[1]?.format(API_DATE_FORMAT);

      updateFlightField(flightId, {
        departDate: formattedDepartDate,
        ...(formattedReturnDate && { returnDate: formattedReturnDate }),
      });
    }

    setIsVisible(departOnly ? !date[0] : !(date[0] && date[1]));
  };

  const handleCancel: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    setDate([
      departDate ? dayjs(departDate, API_DATE_FORMAT) : null,
      returnDate ? dayjs(returnDate, API_DATE_FORMAT) : null,
    ]);
    setIsVisible(false);
  };

  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isVisible]);

  return (
    <>
      <button
        type="button"
        onClick={toggleVisibility}
        className="mobile-date-picker w-full flex flex-row items-center rounded-2xl gap-4 !px-6 border-[1px] 2xl:border-2 dark:border-input border-sleap-gray-400 dark:bg-sleap-gray-950 bg-lightwhite dark:hover:border-white hover:border-sleap-gray-600 text-sleap-gray-950 dark:text-lightwhite h-12"
      >
        {date?.[0] ? (
          <div className="flex w-full items-center date-picker-container gap-x-2">
            <span className="-ms-0.5">
              <CalendarsIcon
                color={resolvedTheme === "dark" ? "offWhite" : "lightblack"}
              />
            </span>
            <div className="flex justify-start items-center w-full mx-auto selected-dates-string">
              <p className="mb-0 text-base font-medium whitespace-nowrap overflow-hidden text-ellipsis">
                {formatDisplayDate(date[0], locale)}
              </p>
              {!departOnly && (
                <Fragment>
                  <span className="ant-picker-range-separator-mobile">-</span>
                  <p className="mb-0 text-base font-medium whitespace-nowrap overflow-hidden text-ellipsis">
                    {formatDisplayDate(date[1], locale)}
                  </p>
                </Fragment>
              )}
            </div>
          </div>
        ) : (
          <div className="flex justify-start gap-x-3 w-full mx-auto ">
            <CalendarsIcon
              color={resolvedTheme === "dark" ? "offWhite" : "lightblack"}
            />

            <div className="flex justify-center">
              <p className="mb-0 text-base font-normal whitespace-nowrap font-switzer overflow-hidden text-ellipsis">
                {t("Other.startDate")}
              </p>
              {!departOnly && (
                <>
                  <span className="ant-picker-range-separator-mobile">-</span>
                  <p className="mb-0 text-base font-normal font-switzer whitespace-nowrap overflow-hidden text-ellipsis">
                    {t("Other.endDate")}
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </button>
      <CalendarModal
        isOpen={isVisible}
        width="w-full bg-lightwhite dark:bg-sleap-gray-950"
        bgColor="bg-lightwhite dark:bg-sleap-gray-950"
      >
        <div
          ref={containerRef}
          className={`mobile-date-picker-container min-w-0 bg-sleap-gray-950 ${!isVisible ? "hidden" : ""}`}
        >
          <div className="flex justify-between items-center p-4">
            <h4 className="text-white font-bold font-unbounded text-lg mb-0 ">
              {t("Other.calendar")}
            </h4>
            <span
              className="p-2 rounded-full bg-sleap-gray-650 cursor-pointer"
              onClick={handleCancel}
            >
              <Cross color={"#fff"} />
            </span>{" "}
          </div>
          <div className="bg-sleap-gray-950 text-lightwhite mx-auto z-50 w-full top-[72px]">
            <div className="flex items-center justify-center gap-x-1">
              {weekdays.map((day, index) => (
                <p key={index} className="mb-0 p-3 text-base font-normal">
                  {day}
                </p>
              ))}
            </div>
          </div>
          <div
            className={`bg-lightwhite dark:bg-sleap-gray-950 ${departOnly && "depart-only"}`}
          >
            {Array.from({ length: 12 }).map((_, index) => (
              <MonthCalendar
                key={index}
                monthOffset={index}
                setIsVisible={setIsVisible}
                date={date}
                setDate={setDate}
                departOnly={departOnly}
              />
            ))}
          </div>

          <div className="dark:border border-sleap-gray-850 bg-sleap-gray-950 px-4 fixed bottom-0  py-4 z-50 w-full">
            <div className="flex flex-col w-full">
              <div className="flex gap-x-2 mb-2 items-center justify-between">
                <div className="font-switzer font-bold text-white text-sm flex flex-col">
                  <span>{t("Other.start")}</span>
                  <span className="text-base">
                    {date?.[0]
                      ? dayjs(date?.[0]).format("ddd, MMM DD")
                      : t("Other.selectDate")}
                  </span>
                </div>{" "}
                {!departOnly && (
                  <div className="font-switzer font-bold text-white text-sm flex flex-col">
                    <span className="text-end">End</span>
                    <span className="text-base">
                      {date?.[1]
                        ? dayjs(date?.[1]).format("ddd, MMM DD")
                        : t("Other.selectDate")}
                    </span>
                  </div>
                )}
              </div>
              <button
                className="flex w-full items-center justify-center rounded-full h-12 bg-sleap-purple-300 px-3 py-2 font-semibold text-white"
                onClick={handleConfirm}
              >
                <h5 className="text-base font-semibold font-inter text-white mb-0">
                  {departOnly
                    ? t("Other.confirmTravelDate")
                    : t("Other.confirmTravelDates")}
                </h5>
              </button>
            </div>
          </div>
        </div>
      </CalendarModal>
    </>
  );
};

export default MobileCalendars;
